import React, { useState } from 'react';

import css from './calculatorStyles.module.css';

// Sizing boundaries for each measurement (in cm).
// Each entry: { letter, min (inclusive), max (inclusive) }.
// The first and last entries use -Infinity / Infinity for open-ended ranges.

const TORSO_RANGES = [
  { letter: 'A', min: -Infinity, max: 37, label: '< 38' },
  { letter: 'B', min: 38, max: 40, label: '38 - 40' },
  { letter: 'C', min: 41, max: 43, label: '41 - 43' },
  { letter: 'D', min: 44, max: 46, label: '44 - 46' },
  { letter: 'E', min: 47, max: 49, label: '47 - 49' },
  { letter: 'F', min: 50, max: 52, label: '50 - 52' },
  { letter: 'G', min: 53, max: 55, label: '53 - 55' },
  { letter: 'H', min: 56, max: Infinity, label: '> 55' },
];

const CHEST_RANGES = [
  { letter: 'A', min: -Infinity, max: 97, label: '<= 97' },
  { letter: 'B', min: 98, max: 110, label: '98 - 110' },
  { letter: 'C', min: 111, max: Infinity, label: '> 110' },
];

const WAIST_RANGES = [
  { letter: 'A', min: -Infinity, max: 76, label: '< 77' },
  { letter: 'B', min: 77, max: 80, label: '77 - 80' },
  { letter: 'C', min: 81, max: 84, label: '81 - 84' },
  { letter: 'D', min: 85, max: 88, label: '85 - 88' },
  { letter: 'E', min: 89, max: 92, label: '89 - 92' },
  { letter: 'F', min: 93, max: 96, label: '93 - 96' },
  { letter: 'G', min: 97, max: 100, label: '97 - 100' },
  { letter: 'H', min: 101, max: 103, label: '101 - 103' },
  { letter: 'I', min: 104, max: 107, label: '104 - 107' },
  { letter: 'J', min: 108, max: Infinity, label: '> 107' },
];

const LEGS_RANGES = [
  { letter: 'A', min: -Infinity, max: 60, label: '< 61' },
  { letter: 'B', min: 61, max: 65, label: '61 - 65' },
  { letter: 'C', min: 66, max: 70, label: '66 - 70' },
  { letter: 'D', min: 71, max: 75, label: '71 - 75' },
  { letter: 'E', min: 76, max: 85, label: '76 - 85' },
  { letter: 'F', min: 86, max: 95, label: '86 - 95' },
  { letter: 'G', min: 96, max: 100, label: '96 - 100' },
  { letter: 'H', min: 101, max: Infinity, label: '> 100' },
];

const BOUNDARY_THRESHOLD = 0.6;

const MEASUREMENT_CONFIGS = [
  { key: 'torso', label: 'Torso', description: 'Main Lift Web', ranges: TORSO_RANGES },
  { key: 'chest', label: 'Chest', description: 'Chest Strap', ranges: CHEST_RANGES },
  { key: 'waist', label: 'Waist', description: 'Lumbar / Lateral', ranges: WAIST_RANGES },
  { key: 'legs', label: 'Legs', description: 'Leg Pad & Strap', ranges: LEGS_RANGES },
];

/**
 * Find the letter code for a given measurement value within a set of ranges.
 * Rounds to the nearest integer since the sizing chart uses whole-number boundaries.
 */
function findLetter(value, ranges) {
  const rounded = Math.round(value);
  for (const range of ranges) {
    if (rounded >= range.min && rounded <= range.max) {
      return range.letter;
    }
  }
  return null;
}

// Pre-compute transition points at module scope (static data, no need to recompute per call).
function getTransitionPoints(ranges) {
  const points = [];
  for (let i = 0; i < ranges.length - 1; i++) {
    const currentMax = ranges[i].max;
    const nextMin = ranges[i + 1].min;
    if (isFinite(currentMax) && isFinite(nextMin)) {
      points.push((currentMax + nextMin) / 2);
    }
  }
  return points;
}

const TRANSITIONS = {
  torso: getTransitionPoints(TORSO_RANGES),
  chest: getTransitionPoints(CHEST_RANGES),
  waist: getTransitionPoints(WAIST_RANGES),
  legs: getTransitionPoints(LEGS_RANGES),
};

function isNearBoundary(value, key) {
  return TRANSITIONS[key].some(t => Math.abs(value - t) < BOUNDARY_THRESHOLD);
}

const INITIAL_VALUES = { torso: '', chest: '', waist: '', legs: '' };

const AerodyneCalculator = () => {
  const [values, setValues] = useState(INITIAL_VALUES);
  const [result, setResult] = useState(null);
  const [errors, setErrors] = useState({});

  const handleChange = (key, raw) => {
    setValues(prev => ({ ...prev, [key]: raw }));
    // Clear field-level error on change
    if (errors[key]) {
      setErrors(prev => {
        const next = { ...prev };
        delete next[key];
        return next;
      });
    }
  };

  const validate = () => {
    const nextErrors = {};
    MEASUREMENT_CONFIGS.forEach(({ key, label }) => {
      const raw = values[key];
      if (raw === '' || raw == null) {
        nextErrors[key] = `${label} is required`;
        return;
      }
      const num = parseFloat(raw);
      if (isNaN(num) || num <= 0) {
        nextErrors[key] = `${label} must be a positive number`;
      }
    });
    return nextErrors;
  };

  const handleCalculate = () => {
    const nextErrors = validate();
    setErrors(nextErrors);

    if (Object.keys(nextErrors).length > 0) {
      setResult(null);
      return;
    }

    const breakdown = MEASUREMENT_CONFIGS.map(({ key, label, description, ranges }) => {
      const num = parseFloat(values[key]);
      const letter = findLetter(num, ranges);
      const nearBoundary = isNearBoundary(num, key);
      return { key, label, description, letter, value: num, nearBoundary };
    });

    const code = breakdown.map(b => b.letter).join('-');
    const hasBoundaryWarning = breakdown.some(b => b.nearBoundary);

    setResult({ code, breakdown, hasBoundaryWarning });
  };

  const handleReset = () => {
    setValues(INITIAL_VALUES);
    setResult(null);
    setErrors({});
  };

  return (
    <div className={css.calculator}>
      <div className={css.card}>
        <h4 className={css.sectionTitle}>Enter your measurements</h4>
        <div className={css.grid}>
          {MEASUREMENT_CONFIGS.map(({ key, label }) => (
            <div className={css.field} key={key}>
              <label className={css.label} htmlFor={`calc-${key}`}>
                {label} (cm)
              </label>
              <input
                id={`calc-${key}`}
                className={css.input}
                type="number"
                inputMode="decimal"
                min="0"
                step="0.1"
                placeholder={`e.g. ${key === 'torso' ? '43' : key === 'chest' ? '102' : key === 'waist' ? '86' : '72'}`}
                value={values[key]}
                onChange={e => handleChange(key, e.target.value)}
              />
              <span className={errors[key] ? css.errorVisible : css.error}>
                {errors[key] || '\u00A0'}
              </span>
            </div>
          ))}
        </div>

        <div className={css.actions}>
          <button type="button" className={css.btnPrimary} onClick={handleCalculate}>
            Calculate
          </button>
          <button type="button" className={css.btnSecondary} onClick={handleReset}>
            Reset
          </button>
        </div>

        {result && (
          <>
            <div className={css.results}>
              <div className={css.tile}>
                <span className={css.tileTitle}>TSO Label Code</span>
                <div className={css.tileRight}>
                  <span className={css.tileValue}>{result.code}</span>
                </div>
              </div>
            </div>

            <div className={css.breakdown}>
              <div className={css.breakdownGrid}>
                {result.breakdown.map(({ key, label, letter }) => (
                  <div className={css.pill} key={key}>
                    <span className={css.pillValue}>{letter}</span>
                    <span className={css.pillLabel}>{label}</span>
                  </div>
                ))}
              </div>
            </div>
          </>
        )}
      </div>

      <details className={css.details}>
        <summary className={css.detailsSummary}>View sizing reference table</summary>
        {MEASUREMENT_CONFIGS.map(({ key, label, description, ranges }) => (
          <div className={css.card} key={key}>
            <h4 className={css.sectionTitle}>{description}</h4>
            <table className={css.referenceTable}>
              <thead>
                <tr>
                  <th>Letter</th>
                  <th>{label} (cm)</th>
                </tr>
              </thead>
              <tbody>
                {ranges.map(range => (
                  <tr key={range.letter}>
                    <td>{range.letter}</td>
                    <td>{range.label}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ))}
      </details>
    </div>
  );
};

export default AerodyneCalculator;
